// Section 13 and 22 (stream M4a): read models for the Justice panel (Society tab) and the case
// screen. Thin wrappers over core/queries.ts with the api's `cfg = config` default, plus the
// safety-point breakdown and deterrence the panel's tooltips show (24.5: the same evaluations
// the crime phase uses).
import { config, type Config } from '../config';
import {
  guardsOnDuty,
  hasBuilding,
  justiceOptions as optionsOf,
  latestReport,
  safetyNeed,
  safetyNeeded,
  safetyPointsOf,
  safetyThreats,
  type JusticeOption,
} from '../core/queries';
import type { Evaluation, Factor, GameState } from '../core/types';
import { valueOf } from '../phases/shared/value';
import { readContext } from './context';

/** Every response with why it cannot be used on a case now (null = available). */
export function justiceOptions(state: GameState, cfg: Config = config): JusticeOption[] {
  return optionsOf(state, cfg);
}

/**
 * The responses the justice *policy* may be set to (Section 18): imprisonment needs a jail (a
 * full jail only diverts single cases to restitution); execution needs the unlock unless it is
 * already the policy.
 */
export function justicePolicyOptions(state: GameState, cfg: Config = config): JusticeOption[] {
  return optionsOf(state, cfg).map((o) =>
    o.response === 'imprisonment'
      ? { response: o.response, block: hasBuilding(state, 'jail') ? null : 'no-jail' }
      : o,
  );
}

export interface SafetyOverview {
  /** Safety points of the last completed season (or the season in progress). */
  points: number;
  /** Rows that sum to `points`: the guards' output, then watch posts and a manned palisade. */
  breakdown: Factor[];
  /** Population / 15. */
  needed: number;
  /** The safety need 0..1 as it stands (met at 1). */
  need: number;
  threats: { undetectedKilling: boolean; raidLoss: boolean };
  guardsOnDuty: number;
  /** Deterrence strength (base 1) with its factors; crime is divided by it. */
  deterrence: Evaluation;
  /** 1 / deterrence strength: the factor on everyone's crime chance. */
  crimeFactor: number;
}

export function safetyOverview(state: GameState, cfg: Config = config): SafetyOverview {
  const report = latestReport(state);
  const points = safetyPointsOf(report);
  const breakdown: Factor[] = [];
  const guards = report?.entries.find(
    (e) => e.kind === 'production' && e.jobId === 'guard' && e.resource === 'safetyPoints',
  );
  let guardPoints = 0;
  if (guards?.kind === 'production') {
    guardPoints = guards.amount;
    breakdown.push({
      source: 'job:guard',
      op: 'add',
      value: guards.amount,
      delta: guards.amount,
      note: `${guards.workers} guards`,
    });
  }
  const ctx = readContext(state, cfg);
  const extras = valueOf(ctx, { kind: 'service', service: 'safetyPoints' }).factors.filter(
    (f) => f.op !== 'base',
  );
  const extraTotal = extras.reduce((a, f) => a + f.delta, 0);
  // The report is what the season used: a building finished after production counts from next
  // season, so the rows are scaled to the recorded total.
  const recorded = Math.max(0, points - guardPoints);
  const scale = extraTotal > 0 ? recorded / extraTotal : 1;
  for (const f of extras) breakdown.push({ ...f, delta: f.delta * scale });
  const deterrence = valueOf(ctx, { kind: 'deterrence' });
  return {
    points,
    breakdown,
    needed: safetyNeeded(state, cfg),
    need: safetyNeed(state, cfg),
    threats: safetyThreats(state, cfg),
    guardsOnDuty: guardsOnDuty(state, cfg).length,
    deterrence,
    crimeFactor: deterrence.value > 0 ? 1 / deterrence.value : 1,
  };
}

export {
  CRIME_KINDS,
  JUSTICE_RESPONSES,
  HARSH_RESPONSES,
  type JusticeBlock,
  type JusticeOption,
  type RaidChoice,
  type RaidOutcome,
} from '../core/queries';
